import { ScheduledServicesModel } from './../../models/schedule/scheduledServicesModel';
import { ServicesModel } from './../../models/services/services.model';
import { inject, Injectable } from '@angular/core';
import { SubtotalModel } from '../../../shared/components/subtotal/model/subtotal.model';
import { CampaignModel } from '../../models/campaigns/campaign.model';
import { FordProtect } from '../../models/ford-user-with-vehicles-models/fordProtect.model';
import { quotationVOs } from '../../models/quotation/quotationVO.model';
import { RecallInfoModel } from '../../models/recall/recall-info.model';
import { RevisionHistory } from '../../models/revision/revision-history.model';
import { revisionPartVOs } from '../../models/revision/revision-part-vo.model';
import { RevisionModel } from '../../models/revision/revision.model';
import { ScheduleServices } from '../../models/schedule/schedule-services.model';
import { ScheduleModel } from '../../models/schedule/schedule.model';
import { SelectedServices } from '../../models/services/selected-services.model';
import { IStep } from '../../models/step/step.model';
import { IolmKitModel } from '../../models/revision/iolm-kit.model';
import { IFeatureToggle, IOptionalServiceOrder, ISimpleListSF, IVehicle } from '@agenda-customer-ui/shared';
import { IUserProfileResponse } from '@core/interface';
import { VEHICLE_MODEL, ADVISOR_TYPE } from '../../../utils/constants';
import { DealerService, FeatureToggleCodes, GeneralServicesService, PaymentsService, SessionService } from '@agenda-customer-ui/data-access';
import { IDealerByLocation, IRecall } from '@core/interface';
import { isCSRecall, isFuelTypeElectrical } from '@core/utils';
import { StorageKeys } from '@core/enums';

@Injectable({
  providedIn: 'root',
})
export class ServiceStorage {
  readonly #dealerService = inject(DealerService);
  paymentsService = inject(PaymentsService);
  readonly #generalServicesService = inject(GeneralServicesService);
  private sessionService = inject(SessionService);

  schedule: ScheduleModel = new ScheduleModel();
  dealer!: IDealerByLocation | null;
  nationalId!: string;
  services: SelectedServices = new SelectedServices();
  recall: RecallInfoModel = new RecallInfoModel();
  private _delivery: quotationVOs = new quotationVOs();
  advisor!: number;
  isPickupAndDelivery = false;
  dealerId!: string;
  vehicleModel!: IVehicle;
  washService: ScheduleServices[] = [];
  servicesPrice: SubtotalModel = new SubtotalModel();
  customerId = '';
  user!: IUserProfileResponse;
  skippedStepPickupDelivery = false;
  fordProtectData = new FordProtect();
  isfordProtectToDiscount!: boolean;
  private _isCommercialVehicle!: boolean;
  private _isElectricalVehicle!: boolean;
  private _serviceModel!: ServicesModel;
  private _reschedule = false;
  private _skippedStepServices = false;
  scheduleToCancel!: ScheduledServicesModel | null;
  firstAccess = true;

  currentStep!: IStep;

  originalPrice!: number;
  totalPrice!: number;
  onRequest = false;
  noCost = false;
  IolmFeatureToggle = false;
  isIolm = false;
  iolmParts: IolmKitModel[] = [];
  iolmService: ScheduleServices = new ScheduleServices();
  revisionDefault = false;
  loadedReschedule = false;
  engineCode: string | null = null;
  
  hasPaymentMethod = false;

  // TODO: Criar um Service Order e incluir os objetos abaixo
  campaignsListSF: ISimpleListSF[] = [];
  iolmKit: ISimpleListSF[] = [];
  // TODO: Esse objeto foi criado para acrescentar sempre q o user acrescentar serviços opcionais, seguir esse modelo para Service Order
  optionalServiceOrder: IOptionalServiceOrder = {
    optionalServiceListSF: [],
    subtotal: ''
  };

  // TODO: isso deveria ficar no service de dealer
  public changeDealer(dealer: IDealerByLocation | null, changeRevisionStorage = true): void {
    if (!dealer) {
      this.dealer = null;
      localStorage.removeItem('@Ford-dealerSelected');
      return;
    }

    this.dealer = dealer;
    this.#dealerService.dealer = dealer;
    this.dealerId = dealer.dealerId.toString();
    this.schedule.dealer = dealer.dealerId;

    localStorage.removeItem('@Ford-revision-v4');
    this.#generalServicesService.clearAllSelectedServices();

    if (!this.reschedule) {
      // se existir uma revisao selecionada apagar a seleção porque algumas dealers nao tem o mesmo serviços opcionais
      if ('@Ford-selected-revision-and-optional' in localStorage && changeRevisionStorage) {
        const services = JSON.parse(localStorage.getItem('@Ford-selected-revision-and-optional') as string);
        services.offeredServices = [];
        services.onRequest = false;
        localStorage.setItem('@Ford-selected-revision-and-optional', JSON.stringify(services));
        this.changeServices(
          services.revisionsHistory,
          services.offeredServices,
          services.totalPrice,
          services.onRequest,
          services.revisionPrice,
          services.selectedRevision,
          services.revisionParts,
          services.revisionInfo,
          services.campaigns
        )
      }
    }
    localStorage.setItem('@Ford-dealerSelected', JSON.stringify(dealer));

    this.paymentsService.enabled = dealer.acceptsOnlinePayments;
  }

  public getDealerSelected(): IDealerByLocation {
    return this.dealer as IDealerByLocation;
  }

  public changeCustomerVin(vin: string): void {
    this.schedule.vin = vin;
  }

  public changeCustomerNationalId(nationalID: string): void {
    this.nationalId = nationalID;
  }

  public changeCustomerCountry(country: string): void {
    this.schedule.country = country;
  }

  public changePickupDelivery(service: quotationVOs) {
    this._delivery = service;
  }

  public getDeliveryService(): quotationVOs {
    return this._delivery
  }

  public changeDelaerId(dealerId: string): void {
    this.dealerId = dealerId;
  }

  public changeCustomerId(customerId: string): void {
    this.customerId = customerId;
  }

  public getCustomerId(): string {
    return this.customerId;
  }

  public getDelaerId(): string {
    return this.dealerId;
  }

  public changeRecall(recall: IRecall[], recallService: ScheduleServices[], hasRecall: boolean): void {
    this.recall.recall = recall.map(r => {
      r.isCSRecall = isCSRecall(r.fsaType);
      return r;
    });
    this.recall.recallService = recallService;
    this.recall.hasRecall = hasRecall;
  }

  public changeVehicleModel(vehicleModel: IVehicle): void {
    this.vehicleModel = vehicleModel;
  }

  public changeServices(
    revisions: RevisionHistory[],
    optionalServices: ScheduleServices[],
    totalPrice: number,
    onRequest: boolean,
    revisionPrice: number,
    revisionSelected: ScheduleServices,
    revisionParts: revisionPartVOs[],
    revisionInfo: RevisionModel[],
    campaigns: CampaignModel[],
  ) {
    this.services.revisionHistory = revisions;
    this.services.offeredServices = optionalServices;
    this.services.totalPrice = totalPrice;
    this.services.onRequest = onRequest;
    this.services.revisionPrice = revisionPrice;
    this.services.revisionParts = revisionParts;
    this.services.revisionInfo = revisionInfo;
    this.services.campaigns = campaigns;

    if (JSON.stringify(revisionSelected) !== "{}") {
      this.services.selectedRevision = revisionSelected;
    }

    localStorage.setItem('@Ford-selected-revision-and-optional', JSON.stringify(this.services));
  }

  resetAllServices() {
    this.services = new SelectedServices();
    this.setIolmParts([]);
    localStorage.setItem('@Ford-revision-v4', '');
    localStorage.setItem('@Ford-selected-revision-and-optional', JSON.stringify(this.services));
  }

  // TODO: apagar depois da entrega do novo step 3
  public setDetails(details: string): void {
    this.services.details = details;
    sessionStorage.setItem('observations', this.services.details);
  }

  // TODO: apagar depois da entrega do novo step 3
  public getDetails(): string {
    if ('observations' in sessionStorage) return sessionStorage.getItem('observations') as string;
    return this.services.details;
  }

  public setLoadedReschedule(value: boolean) {
    this.loadedReschedule = value;
  }

  public getLoadedReschedule(): boolean {
    return this.loadedReschedule;
  }

  public setHasPaymentMethod(value: boolean): void {
    this.hasPaymentMethod = value;
  }

  public getHasPaymentMethod(): boolean {
    return this.hasPaymentMethod;
  }

  public getServices(): SelectedServices {
    return this.services;
  }

  public changeWashService(service: ScheduleServices[]) {
    this.washService = service;
  }

  public changeDateTimeInfo(advisor: number, scheduleDate: string, scheduleHour: number) {
    this.schedule.advisor = advisor;
    this.schedule.scheduleDate = scheduleDate;
    this.schedule.scheduleHour = scheduleHour;
  }

  public changeServicesPrice(subtotal: SubtotalModel) {
    this.servicesPrice = subtotal;
  }

  public setScheduleSource(scheduleSource: string) {
    this.schedule.scheduleSource = scheduleSource;
  }

  public getScheduleSource(): string {
    return this.schedule.scheduleSource;
  }

  public getSkippedStepPickupDelivery(): boolean {
    return this.skippedStepPickupDelivery;
  }

  public setSkippedStepPickupDelivery(value: boolean): void {
    this.skippedStepPickupDelivery = value;
  }

  public changeRevisionByValidate(revisions: RevisionModel[]) {
    this.services.revisionPrice = revisions[0].price;
  }

  public changeRevisionPriceToOnRequest() {
    this.services.revisionPrice = 0;
  }

  public setFordProtect(fordProtectData: FordProtect) {
    this.fordProtectData = fordProtectData;
  }

  public setFordProtectToDiscount(isToDiscount: boolean) {
    this.isfordProtectToDiscount = isToDiscount;
  }

  public setIsCommercialVehicle(isCommercial: boolean): void {
    this._isCommercialVehicle = isCommercial;
  }

  public getIsCommercialVehicle(): boolean {
    return this._isCommercialVehicle;
  }

  public setIsElectricalVehicle(fuelType: string): void {
    this._isElectricalVehicle = isFuelTypeElectrical(fuelType);
  }

  public getIsElectricalVehicle(): boolean {
    return this._isElectricalVehicle;
  }

  public setStep(step: IStep): void {
    this.currentStep = step;
    localStorage.setItem('@Ford-last-step', JSON.stringify({ value: step.value, step: step.step }));
  }

  public getStep(): IStep {
    return this.currentStep;
  }

  public setVehicleDate(vehicle: IVehicle): void {
    this.vehicleModel = vehicle;
    this.schedule.model = vehicle?.modelName;
    this.schedule.vehiclePlate = vehicle?.plate;
    this.schedule.group = vehicle?.modelGroup;
    if (this.vehicleModel) {
      this.vehicleModel.getAdvisorType = this.getAdvisorType.bind(this.vehicleModel);
      this.vehicleModel.isTransit = this.isTransit.bind(this.vehicleModel);
    }
  }

  public getVehicleDate = (): IVehicle => {
    const localVehicleModel = localStorage.getItem('@Ford-vehicleModel');
    if (!this.vehicleModel && localVehicleModel) {
      this.setVehicleDate(JSON.parse(localVehicleModel));
    }
    return this.vehicleModel;
  };

  private getAdvisorType = (modelName: string): string => this.isTransit(modelName) ? ADVISOR_TYPE.REGULAR_TRANSIT : ADVISOR_TYPE.REGULAR;
  private isTransit = (modelName: string): boolean => modelName?.toUpperCase().includes(VEHICLE_MODEL.TRANSIT);

  public getIolmFeatureToggle(): boolean {
    return this.IolmFeatureToggle;
  }

  public setIolmFeatureToggle(IolmFeatureToggle: boolean): void {
    this.IolmFeatureToggle = IolmFeatureToggle;
  }

  public getIsIolm(): boolean {
    return this.isIolm;
  }

  public setIsIolm(isIolm: boolean): void {
    this.isIolm = isIolm;
  }

  public getIolmParts(): IolmKitModel[] {
    return this.iolmParts;
  }

  public setIolmParts(iolmParts: IolmKitModel[]): void {
    this.iolmParts = iolmParts;
  }

  public getIolmService(): ScheduleServices {
    return this.iolmService;
  }

  public setIolmService(iolmService: ScheduleServices): void {
    this.iolmService = iolmService;
  }

  public setRevisionListStorage(serviceModel: ServicesModel): void {
    this._serviceModel = serviceModel;
  }

  public getRevisionListStorage(): ServicesModel {
    const revisionV4 = localStorage.getItem('@Ford-revision-v4') ? JSON.parse(localStorage.getItem('@Ford-revision-v4') as string) : null;
    return revisionV4;
  }

  get reschedule(): boolean {
    return this._reschedule;
  }

  set reschedule(value: boolean) {
    this._reschedule = value;
  }

  get skippedStepServices(): boolean {
    return this._skippedStepServices;
  }

  set skippedStepServices(value: boolean) {
    this._skippedStepServices = value;
  }

  public getEngineCode(): string | null {
    return this.engineCode;
  }

  public setEngineCode(engineCode: string | null): void {
    this.engineCode = engineCode;
  }

  getFeatureToggleFromSession(feature: FeatureToggleCodes): boolean {
    const featureToggles = this.sessionService.getObject<{ [key in FeatureToggleCodes]?: IFeatureToggle }>(StorageKeys.FEATURE_TOGGLE);
    return featureToggles?.[feature]?.active ?? false;
  }

  get paymentFeatureToggleEnabled(): boolean {
    return this.getFeatureToggleFromSession(FeatureToggleCodes.OSR_ONLINE_PAYMENTS_SCALE_UP);
  }

  get favoriteDealerFeatureToggleEnabled(): boolean {
    return this.getFeatureToggleFromSession(FeatureToggleCodes.OSR_SA_FAVORITE_DEALER_AFOSR_2670);
  }
}
